import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';
import { TimeEventService } from '@intranet/modules/timer/time-event.service';
import { TimerToastService } from '@intranet/modules/timer/timer-toast/timer-toast.service';

@Component({
    selector: 'timer-start-button',
    templateUrl: './timer-start-button.component.html',
    styleUrls: ['./timer-start-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimerStartButtonComponent implements OnInit, OnDestroy {
    @Input() jobId!: number;
    @Input() jobNumber!: string | number;
    @Input() jobTitle!: string;
    @Input() clientName!: string;
    @Input() description = '';
    @Input() isBillable = false;
    /** Optional tooltip text for the play (start) button. When unset, no
     *  tooltip is shown — keeps the default usage (action-table, etc.)
     *  uncluttered. The dashboard opts in by passing 'Start timer'. */
    @Input() startTooltip = '';
    /** Optional tooltip text for the stop button. Same opt-in pattern. */
    @Input() stopTooltip = '';
    /** When true, the running state requires the description to match as well
     *  as the jobId. Used on the jobs-detail page where every action row shares
     *  the same jobId, so without this every row would show the stop button.
     */
    @Input() matchDescription = false;
    /** Visual size of the button. Default matches inline-table use (24px);
     *  'large' bumps to 35px to align with header nav controls. */
    @Input() size: 'default' | 'large' = 'default';

    @HostBinding('class.visible') get isVisible() { return this.isRunning; }
    @HostBinding('class.tsb-large') get isLargeSize() { return this.size === 'large'; }

    isRunning = false;
    private sub = new Subscription();

    constructor(
        private timerEntryService: TimerEntryService,
        private timeEventService: TimeEventService,
        private cdr: ChangeDetectorRef,
        private toastService: TimerToastService,
    ) {}

    ngOnInit() {
        this.isRunning = this.timerEntryService.isLiveTimerRunning &&
                         this.matches(
                             this.timerEntryService.liveJobId,
                             this.timerEntryService.liveEntry?.description,
                         );

        this.sub.add(
            this.timerEntryService.newEntry$.subscribe(entry => {
                this.isRunning = this.matches(entry.jobId, entry.description);
                this.cdr.markForCheck();
            })
        );
        this.sub.add(
            this.timerEntryService.stopEntry$.subscribe(() => {
                this.isRunning = false;
                this.cdr.markForCheck();
            })
        );
        this.sub.add(
            this.timerEntryService.updateLiveJob$.subscribe(data => {
                // LiveJobUpdate doesn't carry the description; pull it from
                // the service's tracked live entry.
                const desc = this.timerEntryService.liveEntry?.description;
                this.isRunning = this.matches(data.jobId, desc);
                this.cdr.markForCheck();
            })
        );
    }

    private matches(jobId: number | null | undefined, description: string | undefined): boolean {
        if (jobId !== this.jobId) return false;
        if (this.matchDescription) {
            // The timer header normalizes descriptions (capitalizes the first
            // letter, trims them), so a strict equality check would always
            // miss. Compare case-insensitively after trimming.
            const a = (description ?? '').trim().toLowerCase();
            const b = (this.description ?? '').trim().toLowerCase();
            if (a !== b) return false;
        }
        return true;
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }

    start() {
        this.timerEntryService.continueEntry({
            jobId: this.jobId,
            jobNumber: this.jobNumber,
            jobTitle: this.jobTitle,
            clientName: this.clientName,
            description: this.description,
            isBillable: this.isBillable,
        });
    }

    stop() {
        // De-duplicate: bail out if a previous stop attempt is still in
        // flight so a fast double-click doesn't queue another API call.
        if (this.timerEntryService.runningTimerSaveInFlight > 0) return;
        // Pessimistic stop: keep the local timer running until the server
        // confirms. The previous optimistic ordering called `stopEntry()`
        // before the API resolved, so an offline stop would visually clear
        // the timer even though the server still had it running — and the
        // failure was completely silent.
        this.timerEntryService.runningTimerSaveInFlight++;
        this.timeEventService.stopTimer().subscribe({
            next: () => {
                // Server confirmed — NOW emit the local stop. This flips
                // isLiveTimerRunning and notifies subscribers in unison.
                this.timerEntryService.stopEntry();
            },
            complete: () => { this.timerEntryService.runningTimerSaveInFlight--; },
            error: () => {
                this.timerEntryService.runningTimerSaveInFlight--;
                this.toastService.show('Could not stop the timer — please try again.');
            },
        });
    }
}
